import { useState } from "react";
import {
  isAuthAvailable,
  isEmulatorMode,
  signInWithTestUser,
  type SignInSurface,
} from "../lib/auth";
import { useSignInPromptShown } from "../lib/useSignInPromptShown";
import { AuthProviders } from "./AuthProviders";

/**
 * Sign-in body for the auth sheet: Google, Apple and email code via the
 * shared {@link AuthProviders}, plus emulator test users in dev.
 */
export function SignInPanel({
  onSignedIn,
  surface = "account",
  intro = "Sign in to save your rooms to the cloud and pick up where you left off on another device.",
}: {
  onSignedIn?: () => void;
  surface?: SignInSurface;
  /** Lead-in sentence; `null` when the host already explains why. */
  intro?: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const available = isAuthAvailable();
  useSignInPromptShown(surface, available);

  if (!available) {
    return (
      <div className="pt-2">
        <p className="text-sm text-ink-600">
          Sign-in isn't configured for this environment. Add your Firebase web
          SDK config to <code className="bg-ink-100 px-1 rounded">.env.local</code>{" "}
          and reload to enable Google sign-in.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pt-2">
      {intro && <p className="text-sm text-ink-500">{intro}</p>}

      <AuthProviders surface={surface} onSignedIn={() => onSignedIn?.()} />

      {error && (
        <div className="rounded-xl bg-rose-50 ring-1 ring-ink-200 text-rose-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <p className="text-xs text-ink-400">
        By continuing, you agree to let Room Canvas read your name, email, and
        profile photo from the account you sign in with.
      </p>

      <EmulatorTestSignIn
        busy={busy}
        setBusy={setBusy}
        setError={setError}
        onSignedIn={onSignedIn}
      />
    </div>
  );
}

const TEST_USERS: ReadonlyArray<{ email: string; label: string }> = [
  { email: "alice@test.local", label: "Alice" },
  { email: "bob@test.local", label: "Bob" },
  { email: "carol@test.local", label: "Carol" },
];

/**
 * Emulator-only sign-in shortcuts for the multiplayer testing harness.
 * Renders nothing in production builds — Vite inlines `import.meta.env`,
 * so this component's body is tree-shaken away when
 * `VITE_FIREBASE_USE_EMULATORS !== "true"`.
 */
function EmulatorTestSignIn({
  busy,
  setBusy,
  setError,
  onSignedIn,
}: {
  busy: boolean;
  setBusy: (b: boolean) => void;
  setError: (msg: string | null) => void;
  onSignedIn?: () => void;
}) {
  if (!isEmulatorMode()) return null;

  async function handleClick(email: string) {
    setBusy(true);
    setError(null);
    try {
      await signInWithTestUser(email);
      onSignedIn?.();
    } catch (err) {
      setError((err as Error).message || "Test sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-3">
      <p className="text-caption font-semibold text-amber-900 uppercase tracking-wide">
        Emulator Test Users
      </p>
      <p className="text-caption text-amber-800 mt-1">
        Sign in as a seeded test user against the local Firebase Auth emulator.
        Skips the Google popup so two browser windows can drive multiplayer
        bugs side-by-side.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {TEST_USERS.map((u) => (
          <button
            key={u.email}
            type="button"
            onClick={() => handleClick(u.email)}
            disabled={busy}
            className="h-8 px-3 rounded-md bg-white text-amber-900 ring-1 ring-amber-300 hover:bg-amber-100 text-sm font-medium disabled:opacity-60 disabled:pointer-events-none"
          >
            {u.label}{" "}
            <span className="text-amber-600 font-normal">({u.email})</span>
          </button>
        ))}
      </div>
    </div>
  );
}
